import { jsx as _jsx } from "react/jsx-runtime";
import orderBy from 'lodash/orderBy.js';
import maxBy from 'lodash/maxBy.js';
import invariant from 'invariant';
import { hierarchifyVDResult } from '@cancercentrum/inca';
import { SvgCircle } from './SvgCircle.js';
import { SvgSquare } from './SvgSquare.js';
import { SvgStar } from './SvgStar.js';
/**
 * Diagnos-blocket i patlogiformuläret är en drös med bool-variabler.
 * Detta är kortnamnen på samtliga av de variablerna.
 *
 * Dessa kortnamn MÅSTE vara samma i registret och i VD-mallarna som används för att
 * hämta data och visa upp återkoppling.
 */ export const AllDiagnosShortnames = [
    'ps_ves_sem_vaxt_adenocarcinom',
    'ps_ves_sem',
    'ps_ves_vaxt_adenocarcinom_uns',
    'ps_ves_vaxt_adenocarcinom_prostata',
    'ps_ves_sem_benign_prostata',
    'ps_ves_benign_mesenkymal_vavnad',
    'ps_svarvarderad_kortelatypi',
    'ps_benign',
    'ps_benign_efterregistrering',
    'ps_benign_fokalt_kronisk',
    'ps_benign_utbredd_kronisk',
    'ps_benign_akut',
    'ps_benign_granulomatos',
    'ps_benign_mesenkymal_vavnad',
    'ps_del_av_vesikula',
    'ps_benign_och_del_av_vesikula',
    'ps_ej_bedombart',
    'ps_otillrackligt_material',
    'ps_ingen_vavnad_erhallen',
    'ps_cancer_efterregistrering',
    'ps_acinart_adenocarcinom',
    'ps_atrofisk_cancer',
    'ps_pseudohyperplastisk_cancer',
    'ps_microcystisk_cancer',
    'ps_skumcellig_cancer',
    'ps_mucinos_cancer',
    'ps_signetringcellsliknande_cancer',
    'ps_pleomorf_jattecellscancer',
    'ps_sarkomatoid_cancer',
    'ps_duktalt_adenocarcinom',
    'ps_urotelial_cancer',
    'ps_adenoskvamos_cancer',
    'ps_skivepitelcancer',
    'ps_basalcellscancer',
    'ps_adenocarcinom_neuro_diff',
    'ps_neuroendokrin',
    'ps_neuroendokrin_smacellig',
    'ps_neuroendokrin_storcellig',
    'ps_cancermisstankt_kortelatypi',
    'ps_hoggradig_pin',
    'ps_intraduktalt_carcinom'
];
export const AllDiagnoses = [
    {
        shortname: 'ps_ves_sem_vaxt_adenocarcinom',
        tags: [
            'cancer'
        ],
        label: 'Vesikula seminals med växt av acinärt adenocarcinom'
    },
    {
        shortname: 'ps_ves_sem',
        tags: [
            'benign'
        ],
        label: 'Vesikula seminalis utan tumörväxt'
    },
    {
        shortname: 'ps_ves_vaxt_adenocarcinom_uns',
        tags: [
            'cancer'
        ],
        label: 'Växt av acinärt adenocarcinom, oklart om i prostata eller i vesikula seminalis'
    },
    {
        shortname: 'ps_ves_vaxt_adenocarcinom_prostata',
        tags: [
            'cancer'
        ],
        label: 'Prostatavävnad med växt av acinärt adenocarcinom'
    },
    {
        shortname: 'ps_ves_sem_benign_prostata',
        tags: [
            'benign'
        ],
        label: 'Vesikula seminalis och benign prostatavävnad'
    },
    {
        shortname: 'ps_ves_benign_mesenkymal_vavnad',
        tags: [
            'benign'
        ],
        label: 'Benign mesenkymal vävnad'
    },
    {
        shortname: 'ps_svarvarderad_kortelatypi',
        tags: [
            'benign'
        ],
        label: 'Svårvärderad körtelatypi'
    },
    {
        shortname: 'ps_benign',
        tags: [
            'benign'
        ],
        label: 'Benign prostatavävnad'
    },
    {
        shortname: 'ps_benign_efterregistrering',
        tags: [
            'benign'
        ],
        label: 'Benign'
    },
    {
        shortname: 'ps_benign_fokalt_kronisk',
        tags: [
            'benign'
        ],
        parent: 'ps_benign',
        label: 'Fokalt kronisk inflammation'
    },
    {
        shortname: 'ps_benign_utbredd_kronisk',
        tags: [
            'benign'
        ],
        parent: 'ps_benign',
        label: 'Utbredd kronisk inflammation'
    },
    {
        shortname: 'ps_benign_akut',
        tags: [
            'benign'
        ],
        parent: 'ps_benign',
        label: 'Akut inflammation'
    },
    {
        shortname: 'ps_benign_granulomatos',
        tags: [
            'benign'
        ],
        parent: 'ps_benign',
        label: 'Granulomatös inflammation'
    },
    {
        shortname: 'ps_benign_mesenkymal_vavnad',
        tags: [
            'benign'
        ],
        label: 'Benign mesenkymal vävnad, inga prostatakörtlar'
    },
    {
        shortname: 'ps_del_av_vesikula',
        tags: [
            'info'
        ],
        label: 'Del av vesikula seminalis'
    },
    {
        shortname: 'ps_benign_och_del_av_vesikula',
        tags: [
            'benign'
        ],
        label: 'Benign prostatavävnad och del av vesikula seminalis/ductus ejaculatorius'
    },
    {
        shortname: 'ps_ej_bedombart',
        tags: [
            'not_assessable'
        ],
        label: 'Ej bedömbart'
    },
    {
        shortname: 'ps_otillrackligt_material',
        tags: [
            'not_assessable'
        ],
        label: 'Otillräckligt material'
    },
    {
        shortname: 'ps_ingen_vavnad_erhallen',
        tags: [
            'not_assessable'
        ],
        label: 'Ingen vävnad erhållen'
    },
    {
        shortname: 'ps_cancer_efterregistrering',
        tags: [
            'cancer'
        ],
        label: 'Cancer'
    },
    {
        shortname: 'ps_acinart_adenocarcinom',
        tags: [
            'cancer'
        ],
        label: 'Acinärt adenocarcinom'
    },
    {
        shortname: 'ps_atrofisk_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Atrofisk cancer (atrofiliknande cancer)'
    },
    {
        shortname: 'ps_pseudohyperplastisk_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Pseudohyperplastisk cancer'
    },
    {
        shortname: 'ps_microcystisk_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Microcystisk cancer'
    },
    {
        shortname: 'ps_skumcellig_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Skumcellig cancer'
    },
    {
        shortname: 'ps_mucinos_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Mucinös cancer'
    },
    {
        shortname: 'ps_signetringcellsliknande_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Signetringcellsliknande cancer'
    },
    {
        shortname: 'ps_pleomorf_jattecellscancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Pleomorf jättecellscancer'
    },
    {
        shortname: 'ps_sarkomatoid_cancer',
        tags: [
            'cancer'
        ],
        parent: 'ps_acinart_adenocarcinom',
        label: 'Sarkomatoid cancer'
    },
    {
        shortname: 'ps_duktalt_adenocarcinom',
        tags: [
            'cancer'
        ],
        label: 'Duktalt adenocarcinom'
    },
    {
        shortname: 'ps_urotelial_cancer',
        tags: [
            'cancer'
        ],
        label: 'Urotelial cancer'
    },
    {
        shortname: 'ps_adenoskvamos_cancer',
        tags: [
            'cancer'
        ],
        label: 'Adenoskvamös cancer'
    },
    {
        shortname: 'ps_skivepitelcancer',
        tags: [
            'cancer'
        ],
        label: 'Skivepitelcancer'
    },
    {
        shortname: 'ps_basalcellscancer',
        tags: [
            'cancer'
        ],
        label: 'Basalcellscancer'
    },
    {
        shortname: 'ps_adenocarcinom_neuro_diff',
        tags: [
            'cancer'
        ],
        label: 'Adenocarcinom med neuroendokrin differentiering'
    },
    {
        shortname: 'ps_neuroendokrin',
        tags: [
            'cancer'
        ],
        label: 'Neuroendokrin tumör (carcinoid)'
    },
    {
        shortname: 'ps_neuroendokrin_smacellig',
        tags: [
            'cancer'
        ],
        label: 'Småcellig neuroendokrin cancer'
    },
    {
        shortname: 'ps_neuroendokrin_storcellig',
        tags: [
            'cancer'
        ],
        label: 'Storcellig neuroendokrin cancer'
    },
    {
        shortname: 'ps_cancermisstankt_kortelatypi',
        tags: [
            'cancer'
        ],
        label: 'Cancermisstänkt körtelatypi'
    },
    {
        shortname: 'ps_hoggradig_pin',
        tags: [
            'cancer'
        ],
        label: 'Höggradig PIN'
    },
    {
        shortname: 'ps_intraduktalt_carcinom',
        tags: [
            'cancer'
        ],
        label: 'Intraductalt carcinom'
    }
]; // as const satisfies Partial<Record<ProvsvarShortnames, DiagMeta>>;
export const AllDiagnosesByShortname = AllDiagnoses.reduce((acc, item)=>{
    acc[item.shortname] = item;
    return acc;
}, {});
export const BenignDiagnosShortnames = AllDiagnoses.filter((x)=>x.tags.includes('benign')).map((x)=>x.shortname);
/**
 * Utöver boolvariablerna som motsvarar diagnoser från WHO-boken så finns det även en del
 * väldigt ovanliga diagnoser. Dessa sparas som text i `d_diagnos_text` i `Diagnos`-tabellen.
 * Denna array innehåller de alternativen.
 */ export const ExtraWHODiagnosis = [
    'Acute myeloid leukaemia',
    'Adenocarcinoma',
    'Angiosarcoma',
    'B lymphoblastic leukaemia/lymphoma',
    'Choriocarcinoma',
    'Chronic lymphocytic leukaemia / small lymphocytic lymphoma',
    'Clear cell adenocarcinoma',
    'Cystadenoma',
    'Diffuse large B-cell lymphoma',
    'Duktalt adenocarcinom (Cribriform)',
    'Duktalt adenocarcinom (Papillary)',
    'Duktalt adenocarcinom (Solid)',
    'Ewing sarcoma',
    'Follicular lymphoma',
    'Gastrointestinal stromal tumour, NOS',
    'Germ cell tumours',
    'Granular cell tumour',
    'Haemangioma',
    'Haemangiopericytoma',
    'Hemangiosarkom',
    'Inflammatory myofibroblastic tumour',
    'Leiomyoma',
    'Leiomyosarcoma',
    'Leiomyosarcoma',
    'Liposarcoma',
    'Lymphomas',
    'Mammary-type myofibroblastoma',
    'Mantle cell lymphoma',
    'Melanoma',
    'Nephroblastoma',
    'Neuroblastoma',
    'Neuroendokrina tumörer',
    'Osteosarcoma',
    'Paraganglioma',
    'Rhabdoid tumour',
    'Rhabdomyosarcoma',
    'Schwannoma',
    'Seminoma',
    'Skivepitelneoplasi',
    'Solitary fibrous tumour',
    'Solitary fibrous tumour',
    'Solitary fibrous tumour, malignant',
    'Squamous cell carcinoma',
    'Stromal sarcoma',
    'Stromal tumour of uncertain malignant potential',
    'Synovial sarcoma',
    'Undifferentiated pleomorphic sarcoma',
    'Well-differentiated neuroendocrine tumour'
];
export const getDiagnosisTexts = (ps)=>{
    const result = AllDiagnoses.reduce((acc, dia)=>{
        if (ps.data[dia.shortname]) {
            acc.push(dia.label);
        }
        return acc;
    }, []);
    ps.subTables.Diagnos.forEach((x)=>{
        result.push(x.data.d_diagnos_text);
    });
    return result;
};
// Sektorer som inte är del av A, B eller C. Dvs seminal vesicle eller nedre sfinkter. Dessa har inga officiella korta benämningar och
// ska därmed inte visas i alla lägen i gränssnittet.
const CustomSectors = [
    '1S',
    '4S',
    '1Fv',
    '4Fv',
    '1Fd',
    '4Fd'
];
const SectorNameInfo = {
    '1S': {
        long: 'seminal vesicle på höger sida',
        short: 'Hö Ves Sem'
    },
    '4S': {
        long: 'seminal vesicle på vänster sida',
        short: 'Vä Ves Sem'
    },
    '1Ad': {
        long: 'dorsolateralt i basen på höger sida'
    },
    '1Bd': {
        long: 'dorsolateralt i mittportionen på höger sida'
    },
    '1Cd': {
        long: 'dorsolateralt i apex på höger sida'
    },
    '1Av': {
        long: 'ventrolateralt i basen på höger sida'
    },
    '1Bv': {
        long: 'ventrolateralt i mittportionen på höger sida'
    },
    '1Cv': {
        long: 'ventrolateralt i apex på höger sida'
    },
    '2Ad': {
        long: 'dorsalt i basen på höger sida'
    },
    '2Bd': {
        long: 'dorsalt i mittportionen på höger sida'
    },
    '2Cd': {
        long: 'dorsalt i apex på höger sida'
    },
    '2Av': {
        long: 'ventralt i basen på höger sida'
    },
    '2Bv': {
        long: 'ventralt i mittportionen på höger sida'
    },
    '2Cv': {
        long: 'ventralt i apex på höger sida'
    },
    '3Ad': {
        long: 'dorsalt i basen på vänster sida'
    },
    '3Bd': {
        long: 'dorsalt i mittportionen på vänster sida'
    },
    '3Cd': {
        long: 'dorsalt i apex på vänster sida'
    },
    '3Av': {
        long: 'ventralt i basen på vänster sida'
    },
    '3Bv': {
        long: 'ventralt i mittportionen på vänster sida'
    },
    '3Cv': {
        long: 'ventralt i apex på vänster sida'
    },
    '4Ad': {
        long: 'dorsolateralt i basen på vänster sida'
    },
    '4Bd': {
        long: 'dorsolateralt i mittportionen på vänster sida'
    },
    '4Cd': {
        long: 'dorsolateralt i apex på vänster sida'
    },
    '4Av': {
        long: 'ventrolateralt i basen på vänster sida'
    },
    '4Bv': {
        long: 'ventrolateralt i mittportionen på vänster sida'
    },
    '4Cv': {
        long: 'ventrolateralt i apex på vänster sida'
    },
    '1Fv': {
        long: 'ventrolateralt i nedre sfinkter på höger sida',
        short: 'ventrolateralt i nedre sfinkter på höger sida'
    },
    '4Fv': {
        long: 'ventrolateralt i nedre sfinkter på vänster sida',
        short: 'ventrolateralt i nedre sfinkter på vänster sida'
    },
    '1Fd': {
        long: 'dorsolateralt i nedre sfinkter på höger sida',
        short: 'dorsolateralt i nedre sfinkter på höger sida'
    },
    '4Fd': {
        long: 'dorsolateralt i nedre sfinkter på vänster sida',
        short: 'dorsolateralt i nedre sfinkter på vänster sida'
    }
};
const sortOrders = [
    '1S',
    '4S',
    '1Av',
    '2Av',
    '3Av',
    '4Av',
    '1Ad',
    '2Ad',
    '3Ad',
    '4Ad',
    '1Bv',
    '2Bv',
    '3Bv',
    '4Bv',
    '1Bd',
    '2Bd',
    '3Bd',
    '4Bd',
    '1Cv',
    '2Cv',
    '3Cv',
    '4Cv',
    '1Cd',
    '2Cd',
    '3Cd',
    '4Cd',
    '1Fv',
    '4Fv',
    '1Fd',
    '4Fd'
];
export function sortBySector(arr, sektorSelector) {
    return orderBy(arr, [
        (item)=>sortOrders.indexOf(sektorSelector(item))
    ], [
        'asc'
    ]);
}
export const calculateISUP = (gleasEtt, gleasTva)=>{
    if (typeof gleasEtt !== 'number' || typeof gleasTva !== 'number') {
        return undefined;
    }
    const sum = gleasEtt + gleasTva;
    if (sum >= 9) {
        return 5;
    }
    if (sum === 8) {
        return 4;
    }
    if (gleasEtt === 4 && gleasTva === 3) {
        return 3;
    }
    if (gleasEtt === 3 && gleasTva === 4) {
        return 2;
    }
    if (sum === 6) {
        return 1;
    }
    return undefined;
};
const benign = (ps)=>{
    if (ps.data.ps_benign || ps.data.ps_benign_efterregistrering || ps.data.ps_benign_mesenkymal_vavnad || ps.data.ps_benign_och_del_av_vesikula || ps.data.ps_benign_akut || ps.data.ps_benign_granulomatos || ps.data.ps_benign_fokalt_kronisk || ps.data.ps_benign_utbredd_kronisk || ps.data.ps_ves_benign_mesenkymal_vavnad || ps.data.ps_ves_sem_benign_prostata) {
        return {
            SvgSymbol: /*#__PURE__*/ _jsx(SvgCircle, {
                fill: "#00b300"
            }),
            className: 'provsvar-status-green'
        };
    }
    return undefined;
};
const atypi = (ps)=>{
    const checkedShortnames = AllDiagnoses.filter((x)=>ps.data[x.shortname]);
    const other = ps.subTables.Diagnos.map((x)=>x.data.d_diagnos_text);
    if (ps.data.ps_svarvarderad_kortelatypi || other.length === 0 && checkedShortnames.length > 0 && checkedShortnames.every((x)=>x.shortname === 'ps_cancermisstankt_kortelatypi')) {
        return {
            SvgSymbol: /*#__PURE__*/ _jsx(SvgCircle, {
                fill: "lightgray"
            }),
            className: 'provsvar-status-lightgray'
        };
    }
    return undefined;
};
const hpin = (ps)=>{
    const checkedShortnames = AllDiagnoses.filter((x)=>ps.data[x.shortname]);
    const other = ps.subTables.Diagnos.map((x)=>x.data.d_diagnos_text);
    if (other.length === 0 && checkedShortnames.length > 0 && checkedShortnames.every((x)=>x.shortname === 'ps_hoggradig_pin')) {
        return {
            SvgSymbol: /*#__PURE__*/ _jsx(SvgCircle, {
                fill: "darkgray"
            }),
            className: 'provsvar-status-darkgray'
        };
    }
    return undefined;
};
const intraducCarcinoma = (ps)=>{
    const checkedShortnames = AllDiagnoses.filter((x)=>ps.data[x.shortname]);
    const other = ps.subTables.Diagnos.map((x)=>x.data.d_diagnos_text);
    if (other.length === 0 && checkedShortnames.length > 0 && checkedShortnames.every((x)=>x.shortname === 'ps_intraduktalt_carcinom')) {
        return {
            SvgSymbol: /*#__PURE__*/ _jsx(SvgCircle, {
                fill: "#a67b5b"
            }),
            className: 'provsvar-status-brown'
        };
    }
    return undefined;
};
const diagnos = (ps)=>{
    const whoDia = AllDiagnoses.filter((x)=>x.tags.includes('cancer') && ps.data[x.shortname]);
    if (whoDia.length || ps.subTables.Diagnos.length) {
        const isup = calculateISUP(ps.data.ps_gleasEtt, ps.data.ps_gleasTva);
        const IsupSymbol = {
            1: /*#__PURE__*/ _jsx(SvgCircle, {
                bold: true,
                fill: "#fff860"
            }),
            2: /*#__PURE__*/ _jsx(SvgSquare, {
                fill: "#fed383"
            }),
            3: /*#__PURE__*/ _jsx(SvgSquare, {
                bold: true,
                fill: "#fc7708"
            }),
            4: /*#__PURE__*/ _jsx(SvgSquare, {
                bold: true,
                fill: "#fb001aaa",
                rotate: 45
            }),
            5: /*#__PURE__*/ _jsx(SvgStar, {
                strokeWidth: 5,
                fill: "#6b0001aa"
            })
        };
        return {
            SvgSymbol: IsupSymbol[isup || -1] || /*#__PURE__*/ _jsx(SvgSquare, {
                fill: "transparent"
            }),
            className: isup ? `provsvar-status-isup-${isup}` : 'provsvar-status-malign-other'
        };
    }
    return undefined;
};
export const getBiopsiSVGData = (provsvar)=>{
    const colorCalcs = [
        benign,
        atypi,
        hpin,
        intraducCarcinoma,
        diagnos
    ];
    const colorClass = colorCalcs.reduce((acc, fn)=>{
        return acc || fn(provsvar);
    }, undefined);
    return colorClass || {
        SvgSymbol: /*#__PURE__*/ _jsx(SvgCircle, {
            fill: "transparent"
        }),
        className: ''
    };
};
export const getSectorName = (sector)=>{
    return SectorNameInfo[sector]?.long || sector;
};
export const getShortSectorName = (sector)=>{
    return SectorNameInfo[sector]?.short || sector;
};
export const getLongSectorName = (sector)=>{
    const sectorName = getSectorName(sector);
    return CustomSectors.includes(sector) ? sectorName : `${sector} - ${sectorName}`;
};
/**
 * Normaliserar data som hämtats från vyn "VD - patologi (inkl provsvar)" (ID 4091).
 */ export const normalizePatologiVd = (vdData)=>{
    invariant(Array.isArray(vdData), 'vdData is expected to be an array.');
    const items = hierarchifyVDResult(vdData, [
        {
            tableName: 'Registrering',
            idCol: 'REGISTRERING_ID',
            columns: [
                /^r_/
            ],
            subTables: [
                {
                    tableName: 'Lesion',
                    idCol: 'LESION_ID',
                    columns: [
                        /^ln_/
                    ]
                },
                {
                    tableName: 'Provsvar',
                    idCol: 'PROVSVAR_ID',
                    columns: [
                        /^ps_/
                    ],
                    subTables: [
                        {
                            tableName: 'Diagnos',
                            idCol: 'DIAGNOS_ID',
                            columns: [
                                /^d_/
                            ]
                        },
                        {
                            tableName: 'Sektor',
                            idCol: 'SEKTOR_ID',
                            columns: [
                                /^s_/
                            ]
                        }
                    ]
                }
            ]
        }
    ]);
    return items.reduce((acc, item)=>{
        const reg = maxBy(item.subTables.Registrering, (x)=>x.id);
        if (!reg) {
            // eslint-disable-next-line no-console
            console.warn(`Pathology: row ${item.id} does not have any registration row!`);
            return acc;
        }
        reg.subTables.Lesion = orderBy(reg.subTables.Lesion, [
            (x)=>x.data.ln_lesion
        ], [
            'asc'
        ]);
        acc.push({
            id: item.id,
            data: item.data,
            registrering: reg,
            subTables: {
                Registrering: item.subTables.Registrering
            }
        });
        return acc;
    }, []);
};
/**
 * Normaliserar data som hämtats från vyn "VD - Biopsi (inkl prov)" (ID 4088).
 */ export const normalizeBiopsiVd = (vdData)=>{
    invariant(Array.isArray(vdData), 'vdData is expected to be an array.');
    const items = hierarchifyVDResult(vdData, [
        {
            tableName: 'Registrering',
            idCol: 'REGISTRERING_ID',
            columns: [
                /^r_/
            ],
            subTables: [
                {
                    tableName: 'ProvNy',
                    idCol: 'PROVNY_ID',
                    columns: [
                        /^p_/
                    ],
                    subTables: [
                        {
                            tableName: 'Sektor',
                            idCol: 'SEKTOR_ID',
                            columns: [
                                /^s_/
                            ]
                        }
                    ]
                }
            ]
        }
    ]);
    return items.reduce((acc, item)=>{
        const reg = maxBy(item.subTables.Registrering, (x)=>x.id);
        if (!reg) {
            // eslint-disable-next-line no-console
            console.warn(`Biopsy: row ${item.id} does not have any registration row!`);
            return acc;
        }
        acc.push({
            id: item.id,
            data: item.data,
            registrering: reg
        });
        return acc;
    }, []);
};
/**
 * Normaliserar data som hämtats från vyn "VD - Biopsi (inkl prov)" (ID 4088).
 */ export const normalizeMriVd = (vdData)=>{
    invariant(Array.isArray(vdData), 'vdData is expected to be an array.');
    const items = hierarchifyVDResult(vdData, [
        {
            tableName: 'Registrering',
            idCol: 'REGISTRERING_ID',
            columns: [
                /^r_/
            ],
            subTables: [
                {
                    tableName: 'Lesion',
                    idCol: 'LESION_ID',
                    columns: [
                        /^ln_/
                    ],
                    subTables: [
                        {
                            tableName: 'Sektor',
                            idCol: 'SEKTOR_ID',
                            columns: [
                                /^s_/
                            ]
                        },
                        {
                            tableName: 'SVI',
                            idCol: 'SVI_ID',
                            columns: [
                                /^svi_/
                            ]
                        }
                    ]
                }
            ]
        }
    ]);
    return items.reduce((acc, item)=>{
        const reg = maxBy(item.subTables.Registrering, (x)=>x.id);
        if (!reg) {
            // eslint-disable-next-line no-console
            console.warn(`MRI: row ${item.id} does not have any registration row!`);
            return acc;
        }
        acc.push({
            id: item.id,
            data: item.data,
            registrering: reg
        });
        return acc;
    }, []);
};
export const formatDecimal = (val, precision)=>{
    if (typeof val !== 'number' && typeof val !== 'string') {
        return '';
    }
    let res = val;
    if (typeof res === 'string') {
        const parsedNumber = Number.parseFloat(res.replace(',', '.'));
        if (!Number.isNaN(parsedNumber)) {
            res = parsedNumber;
        }
    }
    if (typeof res !== 'number') {
        return res;
    }
    return res.toFixed(precision).replace('.', ',');
};
